/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-continue */
/* eslint-disable no-plusplus */

import { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { usePage } from '../../../contexts/usePageContext';
import { setConfigStorageItem } from '../../../hooks/useConfigStorage';
import { useCurrentStructure } from '../../../hooks/useCurrentStructure';
import {
 createClientConfigurationStart,
 FieldPresetType,
 getClientConfigurationsStart,
 updateClientConfigurationStart,
} from '../../../redux/clientConfiguration/clientConfigurationSlice';
import { RootState } from '../../../redux/store';
import { StructureType } from '../../../types/types';
import { updateConfData } from '../ColModal/utils';
import { TransformedStructure } from './fieldsModalTypes';

interface FieldsModalState {
 root: TransformedStructure[];
 selected: TransformedStructure[];
}

const transformStructure = (
 input: StructureType[],
 parentKey = '',
 translation = '',
): TransformedStructure[] =>
 input.map((item) => {
  const fullKey = parentKey ? `${parentKey}.${item.Name}` : item.Name;

  const transformed = {
   id: fullKey,
   Name: item.Name,
   Type: item.Type,
   Translation: `${translation ? `${translation}.` : ''}${item.Translation}`,
   Key: fullKey,
   InnerStructure: [],
  } as TransformedStructure;

  if (Array.isArray(item.InnerStructure) && item.InnerStructure.length > 0) {
   transformed.InnerStructure = transformStructure(
    item.InnerStructure,
    fullKey,
    `${translation ? `${translation}.` : ''}${item.Translation}`,
   );
  }

  return transformed;
 });

// const filterStructureRecursively = (
//  items: TransformedStructure[],
//  idToRemove: string,
// ): TransformedStructure[] =>
//  items
//   .filter((item) => item.Key !== idToRemove)
//   .map((item) => ({
//    ...item,
//    InnerStructure: item.InnerStructure
//     ? filterStructureRecursively(item.InnerStructure, idToRemove)
//     : [],
//   }));

export const findTransformedStructure = (
 structure: TransformedStructure[],
 key: string,
): TransformedStructure => {
 const isModelChild = key.includes('.');
 const keys = isModelChild ? key.split('.') : [key];
 const stack: { level: TransformedStructure[]; index: number }[] = [
  { level: structure, index: 0 },
 ];

 let currentKeyIndex = 0;
 while (stack.length > 0) {
  const current = stack[stack.length - 1];
  if (current.index >= current.level.length) {
   stack.pop(); // Finished traversing this level
   currentKeyIndex--; // Go up a level in keys
   continue;
  }

  const item = current.level[current.index];
  current.index++;

  if (item.Name === keys[currentKeyIndex]) {
   if (currentKeyIndex === keys.length - 1) {
    // Found the target item
    return item;
   }

   // Descend into InnerStructure
   if (item.InnerStructure && item.InnerStructure.length > 0) {
    stack.push({ level: item.InnerStructure, index: 0 });
    currentKeyIndex++;
   }
  }
 }
 return null as any;
 //  throw new Error(`Structure with key "${key}" not found`);
};
export const useFieldsSettings = ({
 type,
 handleClose,
}: {
 type: 'sidebar' | 'edit' | null;
 handleClose: Dispatch<SetStateAction<boolean>>;
}) => {
 const { pageName } = usePage();
 const dispatch = useDispatch();

 const clientConfigurationSelector = useSelector(
  (state: RootState) => state.clientConfiguration,
 );
 const { clientConfigurations, success, loading, fieldPresets } =
  clientConfigurationSelector;
 const structure = useCurrentStructure();

 const [items, setItems] = useState<FieldsModalState>({
  root: [],
  selected: [],
 });

 const [activeItem, setActiveItem] = useState<TransformedStructure | null>(
  null,
 );

 const transformedStructure = useMemo(
  () => transformStructure(structure),
  [structure],
 );

 useEffect(() => {
  setItems((prev) => ({
   ...prev,
   root: transformedStructure,
  }));
 }, [structure]);

 useEffect(() => {
  if (fieldPresets && type) {
   setItems((prev) => ({
    ...prev,
    selected: (fieldPresets?.[pageName]?.[type] || [])?.map((item) =>
     findTransformedStructure(transformedStructure, item),
    ),
   }));
  }
 }, [fieldPresets]);

 useEffect(() => {
  if (success.updateClientConfiguration) {
   console.log('updateClientConfiguration success', clientConfigurations);
   dispatch(getClientConfigurationsStart({ cached: false }));
   handleClose(false);
  }
 }, [success.updateClientConfiguration]);

 const handleDragStart = (event: DragStartEvent) => {
  const { active } = event;
  const { id } = active;

  setActiveItem(findTransformedStructure(items.root, id as string));
 };

 const handleDragEnd = (event: DragEndEvent) => {
  const { active, over } = event;

  const str = findTransformedStructure(items.root, active.id as string);

  const isInSelected = items.selected.some((item) => item.Key === active.id);

  if (isInSelected) {
   // only sort order
   const activeIndex = items.selected.findIndex(
    (item) => item.Key === active.id,
   );
   const overIndex = items.selected.findIndex((item) => item.Key === over?.id);

   if (activeIndex !== overIndex) {
    setItems((prevState) => ({
     ...prevState,
     selected: arrayMove(prevState.selected, activeIndex, overIndex),
    }));
   }
  } else {
   // add to selected
   setItems((prevState) => ({
    ...prevState,
    // root: filterStructureRecursively(prevState.root, active.id as string),
    selected: [...prevState.selected, str],
   }));
  }
 };

 const removeSelectedField = (id: string) => {
  setItems((prevState) => ({
   ...prevState,
   selected: prevState.selected.filter((item) => item.Key !== id),
  }));
 };

 const removeAllSelectedFields = () => {
  setItems((prevState) => ({
   ...prevState,
   selected: [],
  }));
 };

 const handleApply = () => {
  const currentBackendData = clientConfigurations?.find(
   (item) => item.Parameter === 'fields',
  );

  const itemsObj = items.selected.map((item) => item.Key);

  const currentBackendValue = JSON.parse(
   currentBackendData?.Value || '{}',
  ) as FieldPresetType;

  const val = updateConfData(
   currentBackendData?.Id || '-1',
   {
    ...currentBackendValue,
    [pageName]: {
     ...currentBackendValue[pageName],
     [type as string]: itemsObj,
    },
   },
   'fields',
  );

  if (!currentBackendData?.Id) {
   dispatch(createClientConfigurationStart(val));
  } else {
   dispatch(updateClientConfigurationStart(val));
  }

  setConfigStorageItem({
   page: pageName,
   key: `favorites_${type}`,
   value: itemsObj,
  });
 };

 return {
  items,
  activeItem,
  handleDragStart,
  handleDragEnd,
  handleApply,
  removeSelectedField,
  removeAllSelectedFields,
  loadingClientConfigurations: loading,
 };
};
